'use client'
import { clsx } from 'clsx'
import { useScroll } from 'framer-motion'
import { SwiperSlide } from 'swiper/react'

import { FC, useRef } from 'react'

import { Block } from '@/app/graphql/generated'
import { AnimationScrollTopComponent } from '@/app/shared/components/animation/scroll-top'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { CardComponent } from '@/app/shared/components/express-courses/elements/card'
import { generateStyle } from '@/app/shared/components/express-courses/services/express-courses.services'
import SignUpLessonComponent from '@/app/shared/components/modals/sign-up-lesson/sign-up-lesson.component'
import useScreenWidth from '@/app/shared/hooks/useScreenWidth'
import { IId } from '@/app/shared/interfaces/common.interface'
import { IButton, ITitle } from '@/app/shared/interfaces/content.interface'
import { clearData } from '@/app/shared/services/data-formatter'
import useModal from '@/app/shared/store/modals.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { ContainerComponent } from '@/app/shared/ui/container'
import { SliderComponent } from '@/app/shared/ui/slider'
import { TitleComponent } from '@/app/shared/ui/title'

// interface
interface IExpressCourses extends IId {
  data: Block
}

export interface IExpressCoursesQuery {
  title: ITitle
  button: IButton
}

const ExpressCoursesComponent: FC<Readonly<IExpressCourses>> = ({ data, id }) => {
  const { screenName } = useScreenWidth()
  const containerEl = useRef<HTMLDivElement | null>(null)
  const open = useModal((state) => state.open)

  const basicData = clearData(JSON.parse(data?.contentAll ?? '')) as IExpressCoursesQuery

  const { scrollYProgress } = useScroll({
    target: containerEl,
    offset: ['start end', 'center start'],
  })

  const handleOpenModal = () => {
    open({
      component: SignUpLessonComponent,
      props: {
        title: basicData?.button?.text,
      },
    })
  }

  return (
    <ContainerComponent id={id}>
      <TitleComponent
        className={'mb-[50px] laptop:mb-[62px]'}
        typeTag={basicData?.title?.size}
        animate
      >
        {basicData?.title?.title}
      </TitleComponent>
      <div className={'-ml-[30px] mb-10 w-[calc(100%+60px)] laptop:!hidden'}>
        <SliderComponent
          className={'mb-[30px] [&>.swiper-wrapper]:items-stretch'}
          slidesPerView={1.2}
          spaceBetween={20}
          centeredSlides={true}
          enabled={['mobile', 'tablet'].includes(screenName)}
          arrow
        >
          {data?.expressCourse?.map((el, index) => (
            <SwiperSlide key={`${el?.id}_${index}`} className={'!h-auto'}>
              <CardComponent data={el} key={index} className={clsx(`h-full`)} />
            </SwiperSlide>
          ))}
        </SliderComponent>
      </div>
      <div
        className={clsx(
          'mb-[60px] hidden grid-cols-2 items-center gap-x-5 gap-y-[27px] laptop:grid',
          {
            'grid-cols-3': ['desktop', 'large'].includes(screenName),
            'grid-cols-2': ['laptop'].includes(screenName),
          },
        )}
        ref={containerEl}
      >
        {data?.expressCourse?.map((el, index) => (
          <AnimationScrollTopComponent
            transform={scrollYProgress}
            start={generateStyle(index + 1, ['laptop'].includes(screenName) ? 2 : 3)}
            key={index}
            className={clsx(`h-full laptop:h-fit`)}
          >
            <CardComponent data={el} />
          </AnimationScrollTopComponent>
        ))}
      </div>
      <AnimationShowBlockComponent
        initial={{
          opacity: 0,
          y: 40,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
      >
        <ButtonComponent
          color={'success'}
          size={'lg'}
          icon={'text-primary'}
          className={'mx-auto w-full laptop:w-[480px]'}
          onClick={handleOpenModal}
        >
          {basicData?.button?.text}
        </ButtonComponent>
      </AnimationShowBlockComponent>
    </ContainerComponent>
  )
}

export default ExpressCoursesComponent
